/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Alert, Button, H1, P, TextField } from '@decisiv/ui-components';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import UserArrowRight from '@decisiv/iconix/lib/components/UserArrowRight';
import Key from '@decisiv/iconix/lib/components/Key';
import LocalizedPasswordField from '../../LocalizedPasswordField';
import useLogin from './hooks/useLogin';
import useCheckUsernameSso from './hooks/useCheckUsernameSso';
import { FormsContainer, StyledAlert } from './components/styledComponents';

function ExtraInfo({ html }) {
  return (
    <P
      weight="regular"
      shade={1}
      style={{
        fontSize: '12px',
        textAlign: 'center',
        marginTop: '35px',
        fontWeight: '400',
      }}
    >
      {/* eslint-disable-next-line react/no-danger */}
      <span dangerouslySetInnerHTML={{ __html: html }} />
    </P>
  );
}

ExtraInfo.propTypes = {
  html: PropTypes.string,
};

ExtraInfo.defaultProps = {
  html: '',
};

function PortalLogin({
  dealerId,
  csrfToken,
  translations,
  requestPasswordClick,
  invitationSentClick,
}) {
  const [error, setError] = useState('');
  const [allowPasswordLogin, setAllowPasswordLogin] = useState(false);

  const { loginBody, setLoginBody, postLogin, isDirty } = useLogin(
    dealerId,
    setError,
    csrfToken,
  );

  const handleAllowPasswordLogin = (shouldAllow) => {
    setAllowPasswordLogin(shouldAllow);
    if (shouldAllow) {
      // Focus password field after React renders it
      setTimeout(() => {
        const passwordField = document.querySelector('input[name="password"]');
        if (passwordField) {
          passwordField.focus();
        }
      }, 100);
    }
  };

  const { postCheckSso } = useCheckUsernameSso(
    loginBody.identity,
    handleAllowPasswordLogin,
    setError,
    csrfToken,
    invitationSentClick,
  );

  const onChangeHandler = (e) => {
    setLoginBody({
      ...loginBody,
      [e.target.name]: e.target.value,
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (allowPasswordLogin) {
        if (!isDirty()) postLogin();
      } else if (loginBody.identity) {
        postCheckSso();
      }
    }
  };

  const ButtonsContainer = styled(Flex)`
    justify-content: space-between;
    margin-top: -1px;

    @media screen and (max-width: 320px) {
      flex-direction: column-reverse;

      .btn__element--login-btn {
        margin-bottom: 15px;
      }
    }
  `;

  return (
    <Flex justifyContent="center" alignItems="center" flexDirection="column">
      <FormsContainer>
        <H1>{translations.portalLogin}</H1>
        <Flex
          flexDirection="column"
          style={{
            minWidth: '360px',
          }}
        >
          {error && (
            <StyledAlert>
              <Alert intent="danger" title={error} />
            </StyledAlert>
          )}
          <Flex marginBottom={1}>
            <TextField
              icon={UserArrowRight}
              label={translations.usernameOrEmail}
              name="identity"
              required
              onChange={onChangeHandler}
              onKeyPress={handleKeyPress}
            />
          </Flex>
          {allowPasswordLogin && (
            <>
              <LocalizedPasswordField
                style={{ marginBottom: '10px' }}
                icon={Key}
                label={translations.password}
                showPasswordLabel={translations.showPassword}
                hidePasswordLabel={translations.hidePassword}
                name="password"
                required
                onChange={onChangeHandler}
                onKeyPress={handleKeyPress}
                autoComplete="off"
              />
              <a
                role="button"
                tabIndex={0}
                onClick={() => requestPasswordClick()}
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  marginLeft: 'auto',
                }}
              >
                {translations.forgotPassword}
              </a>
            </>
          )}
          <ButtonsContainer>
            <div className="btn__element--login-btn">
              <Button
                text={translations.logIn}
                onClick={() =>
                  allowPasswordLogin ? postLogin() : postCheckSso()
                }
                disabled={allowPasswordLogin ? isDirty() : !loginBody.identity}
              />
            </div>
          </ButtonsContainer>
        </Flex>
      </FormsContainer>
      <ExtraInfo html={translations.legalAgreementsHtml} />
    </Flex>
  );
}

PortalLogin.propTypes = {
  dealerId: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  translations: PropTypes.objectOf(PropTypes.string),
  requestPasswordClick: PropTypes.func,
  invitationSentClick: PropTypes.func,
};

PortalLogin.defaultProps = {
  dealerId: '',
  translations: {},
  requestPasswordClick: () => {},
  invitationSentClick: () => {},
};

export default PortalLogin;
